import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from "@/lib/logger";
import * as Sentry from "@sentry/react";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  adminId: number | null;
  isAuthorized: boolean;
  /** True after checkAuthorization has run for the current user. 
   *  Distinguishes "not yet checked" from "checked & unauthorized". */
  authResolved: boolean;
  isLoading: boolean;
  sendOtp: (email: string) => Promise<{ error: Error | null }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: Error | null }>;
  sendPhoneOtp: (phone: string) => Promise<{ error: Error | null }>;
  verifyPhoneOtp: (phone: string, token: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const checkingUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // Set Sentry user context for error tracking
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email, phone: session.user.phone });
        } else {
          Sentry.setUser(null);
        }

        // Only show loading on meaningful auth changes, NOT token refreshes.
        if (event === 'TOKEN_REFRESHED') {
          return;
        }

        // Skip re-fetching role if the user hasn't actually changed
        if (event === 'SIGNED_IN' && session?.user?.id === user?.id && isAuthorized) {
          return;
        }

        // Defer role fetch to avoid deadlock
        if (session?.user) {
          // Skip if already checking this user (avoids double-fire with getSession)
          if (checkingUserIdRef.current === session.user.id) return;
          // Keep loading true while fetching role
          setIsLoading(true);
          setAuthResolved(false);
          setTimeout(() => {
            if (isMounted) {
              checkAuthorization(session.user.id);
            }
          }, 0);
        } else {
          checkingUserIdRef.current = null;
          setIsAuthorized(false);
          setAuthResolved(false);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isMounted) return;

      if (error) {
        captureError(error);
        // Clear invalid session
        supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setIsAuthorized(false);
        setAuthResolved(false);
        setIsLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Skip if onAuthStateChange already triggered a check for this user
        if (checkingUserIdRef.current === session.user.id) return;
        checkAuthorization(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkAuthorization = async (userId: string) => {
    checkingUserIdRef.current = userId;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, admin_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        captureError(error);
        setIsAuthorized(false);
        setAdminId(null);
      } else if (data?.admin_id) {
        setIsAuthorized(true);
        setAdminId(data.admin_id);
      } else {
        // No profile found — try to link an existing profile to this auth user
        const { data: linkResult } = await supabase.rpc('link_auth_profile', {
          p_new_user_id: userId,
        });
        if (linkResult?.linked && linkResult.has_admin) {
          // Re-fetch to get admin_id after linking
          const { data: linked } = await supabase
            .from('profiles')
            .select('admin_id')
            .eq('user_id', userId)
            .maybeSingle();
          setIsAuthorized(true);
          setAdminId(linked?.admin_id ?? null);
        } else {
          setIsAuthorized(false);
          setAdminId(null);
        }
      }
    } catch (err) {
      captureError(err);
      setIsAuthorized(false);
    } finally {
      setAuthResolved(true);
      setIsLoading(false);
    }
  };

  const sendOtp = async (email: string) => {
    // Pre-check: verify email exists in profiles
    try {
      const { data, error: rpcError } = await supabase.rpc('prepare_email_login', { p_email: email });
      if (rpcError) {
        return { error: new Error('Failed to validate email') };
      }
      if (data && !data.exists) {
        return { error: new Error('This email address was not found. Please contact support for help.') };
      }
    } catch {
      return { error: new Error('Failed to validate email') };
    }
    const { error } = await supabase.auth.signInWithOtp({ email });
    return { error: error as Error | null };
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { error: error as Error | null };
  };

  const sendPhoneOtp = async (phone: string) => {
    // Pre-check: verify phone exists in profiles
    try {
      const { data, error: rpcError } = await supabase.rpc('prepare_phone_login', { p_phone: phone });
      if (rpcError) {
        return { error: new Error('Failed to validate phone number') };
      }
      if (data && !data.exists) {
        return { error: new Error('This phone number was not found. Please contact support for help.') };
      }
    } catch {
      return { error: new Error('Failed to validate phone number') };
    }

    const { error } = await supabase.auth.signInWithOtp({
      phone,
    });
    return { error: error as Error | null };
  };

  const verifyPhoneOtp = async (phone: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    checkingUserIdRef.current = null;
    await supabase.auth.signOut();
    Sentry.setUser(null);
    setUser(null);
    setSession(null);
    setAdminId(null);
    setIsAuthorized(false);
    setAuthResolved(false);
  };

  const value: AuthContextType = {
    user,
    session,
    adminId,
    isAuthorized,
    authResolved,
    isLoading,
    sendOtp,
    verifyOtp,
    sendPhoneOtp,
    verifyPhoneOtp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
